import { defineStore } from "pinia";
import { computed, ref } from "vue";

import usePartnership from "../api/usePartnership";
import type { Partnership, Vendor } from "../types";
import { PARTNERSHIPS } from "../types";

import { UserRole, type User } from "@/shared/types/user";

export const usePartnershipStore = defineStore("partnership", () => {
  const { switchPartnership: switchPartnershipRequest, fetchVendors: fetchVendorsRequest }
    = usePartnership();

  const partnership = ref<Partnership>(PARTNERSHIPS.SellerCentral);
  const partnerships = ref<Partnership[]>([]);

  // Vendor-central analog of brandShortNames — gates vendor-brand access
  // on Vendor Central pages. Not rendered in the switcher UI.
  const vendorBrandShortNames = ref<string[]>([]);

  const vendors = ref<Vendor[]>([]);
  const vendorsLoading = ref(false);

  const switching = ref(false);
  const isInitialized = ref(false);

  // Captured from /me — the switch endpoint requires it in the payload
  const userId = ref<string | null>(null);

  const isVendorMode = computed(() => partnership.value === PARTNERSHIPS.VendorCentral);

  // Repeated calls are cheap: the request is auto-key cached (swr, 1h fresh / 5h stale)
  const loadVendors = async (): Promise<void> => {
    vendorsLoading.value = true;

    try {
      const { execute } = fetchVendorsRequest();
      const response = await execute();
      if (response) vendors.value = response;
    }
    finally {
      vendorsLoading.value = false;
    }
  };

  /**
   * Hydrate partnership state from the /me user payload.
   * Idempotent — the auth guard calls it on every navigation.
   */
  const initFromUser = (source: User): void => {
    if (isInitialized.value) return;

    userId.value = source.id;
    partnership.value = source.partnership ?? PARTNERSHIPS.SellerCentral;

    // Admins can switch to any central; regular users get the /me list
    partnerships.value = source.role === UserRole.ADMIN
      ? [PARTNERSHIPS.SellerCentral, PARTNERSHIPS.VendorCentral]
      : source.partnerships ?? [];

    vendorBrandShortNames.value = source.vendorBrandShortNames ?? [];
    isInitialized.value = true;

    if (partnership.value === PARTNERSHIPS.VendorCentral) void loadVendors();
  };

  const switchPartnership = async (target: Partnership): Promise<boolean> => {
    if (target === partnership.value || switching.value) return false;
    if (!partnerships.value.includes(target) || !userId.value) return false;

    switching.value = true;

    try {
      const { execute } = switchPartnershipRequest({ lazy: true });
      const response = await execute({ data: { userID: userId.value, partnership: target } });
      if (!response) return false; // error already toasted by the global onError

      partnership.value = response.partnership ?? target;
      if (partnership.value === PARTNERSHIPS.VendorCentral) void loadVendors();
      return true;
    }
    finally {
      switching.value = false;
    }
  };

  // Called on logout — without it a re-login as another user skips initFromUser
  const reset = (): void => {
    userId.value = null;
    partnership.value = PARTNERSHIPS.SellerCentral;
    partnerships.value = [];
    vendorBrandShortNames.value = [];
    vendors.value = [];
    isInitialized.value = false;
  };

  return {
    partnership,
    partnerships,
    isVendorMode,
    vendorBrandShortNames,
    vendors,
    vendorsLoading,
    switching,
    isInitialized,
    initFromUser,
    switchPartnership,
    loadVendors,
    reset,
  };
});
